import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  BarChart3, Camera, Radio, Clock, TrendingUp, 
  ArrowLeft, Calendar, RefreshCw, AlertCircle,
  Eye, Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import api from '@/lib/api';

// ============================================
// ROBUST FIELD MAPPING HELPERS
// ============================================

/**
 * Get a numeric value from an object, trying multiple possible keys
 * Returns null if no valid number found (for N/A display)
 */
const getNum = (obj, keys) => {
  if (!obj) return null;
  for (const key of keys) {
    const val = obj[key];
    if (val !== undefined && val !== null && !isNaN(val)) {
      return Number(val);
    }
  }
  return null;
};

/**
 * Get a string value from an object, trying multiple possible keys
 */
const getStr = (obj, keys, fallback = '') => {
  if (!obj) return fallback;
  for (const key of keys) {
    const val = obj[key];
    if (val !== undefined && val !== null && val !== '') {
      return String(val);
    }
  }
  return fallback;
};

/**
 * Format hours value - supports both decimal hours and minutes
 * Returns "N/A" if value is null/undefined
 */
const formatHoursValue = (value, inputType = 'auto') => {
  if (value === null || value === undefined) return 'N/A';
  
  const num = Number(value);
  if (isNaN(num)) return 'N/A';
  if (num === 0) return '0h';
  
  // Determine if input is hours or minutes
  // If inputType is 'auto', assume hours if value is small (< 100), minutes if large
  let totalMinutes;
  if (inputType === 'hours' || (inputType === 'auto' && num < 100)) {
    // Value is in hours - convert to minutes for formatting
    totalMinutes = num * 60;
  } else {
    // Value is in minutes
    totalMinutes = num;
  }
  
  const hours = Math.floor(totalMinutes / 60);
  const mins = Math.round(totalMinutes % 60);
  
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

/**
 * Format a number for display with K/M suffixes
 * Returns "N/A" if value is null/undefined
 */
const formatNumber = (num) => {
  if (num === null || num === undefined) return 'N/A';
  if (isNaN(num)) return 'N/A';
  if (num === 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

/**
 * Normalize API response to array - handles various response shapes
 */
const normalizeToArray = (data, ...possibleKeys) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  
  for (const key of possibleKeys) {
    if (data[key] && Array.isArray(data[key])) {
      return data[key];
    }
  }
  
  return [];
};

// ============================================
// COMPONENTS
// ============================================

// Custom chart tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border border-border bg-card p-3 shadow-lg">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm text-muted-foreground">
            {entry.name}: <span className="font-medium text-foreground">
              {formatHoursValue(entry.value, 'minutes')}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Camera Analytics Table - Location column REMOVED
const CameraAnalyticsTable = ({ data, onDrilldown }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Camera className="mb-4 h-12 w-12 text-muted-foreground/30" />
        <p className="text-muted-foreground">No camera analytics data available</p>
        <p className="text-sm text-muted-foreground mt-1">Data will appear once cameras have viewing activity</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">#</TableHead>
          <TableHead>Camera</TableHead>
          <TableHead className="text-right">Watch Hours</TableHead>
          <TableHead className="text-right">Sessions</TableHead>
          <TableHead className="text-right">Unique Viewers</TableHead>
          <TableHead className="w-12"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((camera, index) => {
          // Robust field extraction for watch hours
          const watchHours = getNum(camera, [
            'watch_hours', 'watchHours', 'total_watch_hours', 'hours',
            'watch_time_hours', 'totalWatchHours'
          ]);
          const watchMinutes = getNum(camera, [
            'watch_minutes', 'watchMinutes', 'total_minutes', 'minutes',
            'watch_time_minutes', 'totalMinutes'
          ]);
          
          // Prefer hours if available, otherwise use minutes
          let watchDisplay;
          if (watchHours !== null) {
            watchDisplay = formatHoursValue(watchHours, 'hours');
          } else if (watchMinutes !== null) {
            watchDisplay = formatHoursValue(watchMinutes, 'minutes');
          } else {
            watchDisplay = 'N/A';
          }
          
          // Sessions
          const sessions = getNum(camera, [
            'session_count', 'sessions', 'sessionCount', 'total_sessions'
          ]);
          
          // Unique viewers - N/A if not present
          const uniqueViewers = getNum(camera, [
            'unique_viewers', 'uniqueViewers', 'viewers', 'unique_users'
          ]);
          
          return (
            <TableRow 
              key={camera.camera_id || camera.id || camera._id || index}
              className="cursor-pointer hover:bg-secondary/50"
              onClick={() => onDrilldown(camera)}
            >
              <TableCell className="font-medium text-muted-foreground">
                {index + 1}
              </TableCell>
              <TableCell className="font-medium">
                {getStr(camera, ['name', 'camera_name', 'cameraName'], 'Unknown')}
              </TableCell>
              <TableCell className="text-right font-mono">
                {watchDisplay}
              </TableCell>
              <TableCell className="text-right">
                {formatNumber(sessions)}
              </TableCell>
              <TableCell className="text-right">
                {formatNumber(uniqueViewers)}
              </TableCell>
              <TableCell>
                <Button variant="ghost" size="sm">
                  <TrendingUp className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

// Radio Analytics Table
const RadioAnalyticsTable = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Radio className="mb-4 h-12 w-12 text-muted-foreground/30" />
        <p className="text-muted-foreground">No radio analytics data available</p>
        <p className="text-sm text-muted-foreground mt-1">Data will appear once radio stations have listening activity</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">#</TableHead>
          <TableHead>Station</TableHead>
          <TableHead className="text-right">Listen Hours</TableHead>
          <TableHead className="text-right">Sessions</TableHead>
          <TableHead className="text-right">Unique Listeners</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((station, index) => {
          // Robust field extraction for listen hours
          const listenHours = getNum(station, [
            'listen_hours', 'listenHours', 'total_listen_hours', 'hours'
          ]);
          const listenMinutes = getNum(station, [
            'listen_minutes', 'listenMinutes', 'total_minutes', 'minutes'
          ]);
          
          let listenDisplay;
          if (listenHours !== null) {
            listenDisplay = formatHoursValue(listenHours, 'hours');
          } else if (listenMinutes !== null) {
            listenDisplay = formatHoursValue(listenMinutes, 'minutes');
          } else {
            listenDisplay = 'N/A';
          }
          
          const sessions = getNum(station, [
            'session_count', 'sessions', 'sessionCount', 'total_sessions'
          ]);
          
          const uniqueListeners = getNum(station, [
            'unique_listeners', 'uniqueListeners', 'listeners', 'unique_users'
          ]);
          
          return (
            <TableRow key={station.station_id || station.id || station._id || index}>
              <TableCell className="font-medium text-muted-foreground">
                {index + 1}
              </TableCell>
              <TableCell className="font-medium">
                {getStr(station, ['name', 'station_name', 'stationName'], 'Unknown')}
              </TableCell>
              <TableCell className="text-right font-mono">
                {listenDisplay}
              </TableCell>
              <TableCell className="text-right">
                {formatNumber(sessions)}
              </TableCell>
              <TableCell className="text-right">
                {formatNumber(uniqueListeners)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

// Camera Drilldown View
const CameraDrilldown = ({ camera, timeRange, onBack }) => {
  const [dailyData, setDailyData] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDailyData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const cameraId = camera.camera_id || camera.id || camera._id;
        const data = await api.getCameraAnalytics(cameraId, timeRange);
        
        // Get daily array from response
        const daily = data.daily || data.timeline || [];
        setDailyData(daily.map(d => ({
          ...d,
          // Convert watch_seconds to watch_minutes
          watch_minutes: d.watch_seconds ? Math.round(d.watch_seconds / 60) : (d.watch_minutes || 0),
          label: d.date ? new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'
        })));
        
        // Extract viewers array from response
        if (data.viewers && Array.isArray(data.viewers)) {
          setViewers(data.viewers);
        } else {
          setViewers([]);
        }
      } catch (err) {
        setError(err.message);
        setDailyData([]);
        setViewers([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDailyData();
  }, [camera, timeRange]);

  // Extract camera stats with robust field mapping
  const watchHours = getNum(camera, ['watch_hours', 'total_watch_hours', 'hours']);
  const watchMinutes = getNum(camera, ['watch_minutes', 'total_minutes', 'minutes']);
  const totalWatchDisplay = watchHours !== null 
    ? formatHoursValue(watchHours, 'hours')
    : formatHoursValue(watchMinutes, 'minutes');
  
  const sessions = getNum(camera, ['session_count', 'sessions', 'total_sessions']);
  const uniqueViewers = getNum(camera, ['unique_viewers', 'viewers', 'unique_users']);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-overview-btn">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Overview
        </Button>
      </div>

      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">
                {getStr(camera, ['name', 'camera_name'], 'Camera')}
              </CardTitle>
              <CardDescription>
                {getStr(camera, ['location'], 'No location')}
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-sm">
              {totalWatchDisplay} total
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="rounded-lg bg-secondary/50 p-4">
              <p className="text-sm text-muted-foreground">Total Watch Time</p>
              <p className="text-2xl font-bold">{totalWatchDisplay}</p>
            </div>
            <div className="rounded-lg bg-secondary/50 p-4">
              <p className="text-sm text-muted-foreground">Sessions</p>
              <p className="text-2xl font-bold">{formatNumber(sessions)}</p>
            </div>
            <div className="rounded-lg bg-secondary/50 p-4">
              <p className="text-sm text-muted-foreground">Unique Viewers</p>
              <p className="text-2xl font-bold">{formatNumber(uniqueViewers)}</p>
            </div>
          </div>

          <div className="h-64">
            <p className="text-sm font-medium mb-4">Daily Watch Hours</p>
            {isLoading ? (
              <div className="h-full animate-pulse bg-muted rounded" />
            ) : error ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <AlertCircle className="mr-2 h-5 w-5" />
                Failed to load chart data
              </div>
            ) : dailyData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No daily data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyData}>
                  <defs>
                    <linearGradient id="colorWatch" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F7931E" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#F7931E" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(217.2 32% 17.5%)" />
                  <XAxis 
                    dataKey="label" 
                    stroke="hsl(215 20% 65%)"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="hsl(215 20% 65%)"
                    fontSize={12}
                    tickFormatter={(v) => formatHoursValue(v, 'minutes')}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="watch_minutes" 
                    name="Watch Time"
                    stroke="#F7931E" 
                    fill="url(#colorWatch)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Viewers Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Viewers
          </CardTitle>
          <CardDescription>
            Individual viewer statistics for this camera
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-32 animate-pulse bg-muted rounded" />
          ) : viewers.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              No viewer data available
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead className="text-right">Watch Time</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                  <TableHead className="text-right">Last Watched</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {viewers.map((viewer, index) => (
                  <TableRow key={viewer.username || index}>
                    <TableCell className="font-medium">{viewer.username || 'Unknown'}</TableCell>
                    <TableCell className="text-right">
                      {viewer.watch_hours 
                        ? formatHoursValue(viewer.watch_hours, 'hours')
                        : viewer.watch_seconds 
                        ? formatHoursValue(viewer.watch_seconds / 3600, 'hours')
                        : 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">{viewer.session_count || viewer.sessions || 0}</TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {viewer.last_watched 
                        ? new Date(viewer.last_watched).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================
// MAIN ANALYTICS COMPONENT
// ============================================

export function Analytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [timeRange, setTimeRange] = useState(searchParams.get('range') || '7d');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'cameras');
  const [summary, setSummary] = useState(null);
  const [cameraData, setCameraData] = useState([]);
  const [radioData, setRadioData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCamera, setSelectedCamera] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [summaryResult, camerasResult, radioResult] = await Promise.allSettled([
        api.getAnalyticsSummary(timeRange),
        api.getCamerasAnalytics(timeRange),
        api.getRadioAnalytics(timeRange)
      ]);
      
      if (summaryResult.status === 'fulfilled') {
        setSummary(summaryResult.value);
      } else {
        setSummary(null);
      }
      
      if (camerasResult.status === 'fulfilled') {
        const camData = normalizeToArray(camerasResult.value, 'cameras', 'data', 'streams');
        setCameraData(camData);
      } else {
        setCameraData([]);
      }
      
      if (radioResult.status === 'fulfilled') {
        const radData = normalizeToArray(radioResult.value, 'stations', 'data', 'streams');
        setRadioData(radData);
      } else {
        setRadioData([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    setSearchParams({ range, tab: activeTab });
    setSelectedCamera(null);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ range: timeRange, tab });
  };

  // Calculate summary stats with robust field mapping
  const summaryStats = useMemo(() => {
    // Try to get values from summary first
    const watchHoursFromSummary = summary ? getNum(summary, [
      'cameras.total_watch_hours', 'total_watch_hours', 'totalWatchHours',
      'watch_hours', 'watchHours'
    ]) : null;
    
    const watchMinutesFromSummary = summary ? getNum(summary, [
      'cameras.total_watch_minutes', 'total_camera_minutes', 'total_watch_minutes',
      'totalCameraMinutes', 'watch_minutes'
    ]) : null;
    
    const listenHoursFromSummary = summary ? getNum(summary, [
      'radio.total_listen_hours', 'total_listen_hours', 'totalListenHours',
      'listen_hours', 'listenHours'
    ]) : null;
    
    const listenMinutesFromSummary = summary ? getNum(summary, [
      'radio.total_listen_minutes', 'total_radio_minutes', 'total_listen_minutes',
      'totalRadioMinutes', 'listen_minutes'
    ]) : null;
    
    const sessionsFromSummary = summary ? getNum(summary, [
      'total_sessions', 'totalSessions', 'session_count', 'sessions'
    ]) : null;
    
    // Also check nested structure
    const nestedWatchHours = summary?.cameras ? getNum(summary.cameras, [
      'total_watch_hours', 'watch_hours', 'hours'
    ]) : null;
    
    const nestedListenHours = summary?.radio ? getNum(summary.radio, [
      'total_listen_hours', 'listen_hours', 'hours'
    ]) : null;
    
    // Determine watch time
    let totalWatchDisplay;
    if (watchHoursFromSummary !== null) {
      totalWatchDisplay = formatHoursValue(watchHoursFromSummary, 'hours');
    } else if (nestedWatchHours !== null) {
      totalWatchDisplay = formatHoursValue(nestedWatchHours, 'hours');
    } else if (watchMinutesFromSummary !== null) {
      totalWatchDisplay = formatHoursValue(watchMinutesFromSummary, 'minutes');
    } else if (Array.isArray(cameraData) && cameraData.length > 0) {
      // Fallback: sum from camera data
      let totalMins = 0;
      let hasData = false;
      cameraData.forEach(c => {
        const hrs = getNum(c, ['watch_hours', 'total_watch_hours', 'hours']);
        const mins = getNum(c, ['watch_minutes', 'total_minutes', 'minutes']);
        if (hrs !== null) {
          totalMins += hrs * 60;
          hasData = true;
        } else if (mins !== null) {
          totalMins += mins;
          hasData = true;
        }
      });
      totalWatchDisplay = hasData ? formatHoursValue(totalMins, 'minutes') : 'N/A';
    } else {
      totalWatchDisplay = 'N/A';
    }
    
    // Determine listen time
    let totalListenDisplay;
    if (listenHoursFromSummary !== null) {
      totalListenDisplay = formatHoursValue(listenHoursFromSummary, 'hours');
    } else if (nestedListenHours !== null) {
      totalListenDisplay = formatHoursValue(nestedListenHours, 'hours');
    } else if (listenMinutesFromSummary !== null) {
      totalListenDisplay = formatHoursValue(listenMinutesFromSummary, 'minutes');
    } else if (Array.isArray(radioData) && radioData.length > 0) {
      let totalMins = 0;
      let hasData = false;
      radioData.forEach(r => {
        const hrs = getNum(r, ['listen_hours', 'total_listen_hours', 'hours']);
        const mins = getNum(r, ['listen_minutes', 'total_minutes', 'minutes']);
        if (hrs !== null) {
          totalMins += hrs * 60;
          hasData = true;
        } else if (mins !== null) {
          totalMins += mins;
          hasData = true;
        }
      });
      totalListenDisplay = hasData ? formatHoursValue(totalMins, 'minutes') : 'N/A';
    } else {
      totalListenDisplay = 'N/A';
    }
    
    // Determine sessions
    let totalSessionsDisplay;
    if (sessionsFromSummary !== null) {
      totalSessionsDisplay = formatNumber(sessionsFromSummary);
    } else {
      // Fallback: sum from camera + radio data
      let total = 0;
      let hasData = false;
      if (Array.isArray(cameraData)) {
        cameraData.forEach(c => {
          const s = getNum(c, ['session_count', 'sessions']);
          if (s !== null) {
            total += s;
            hasData = true;
          }
        });
      }
      if (Array.isArray(radioData)) {
        radioData.forEach(r => {
          const s = getNum(r, ['session_count', 'sessions']);
          if (s !== null) {
            total += s;
            hasData = true;
          }
        });
      }
      totalSessionsDisplay = hasData ? formatNumber(total) : 'N/A';
    }
    
    return {
      watchDisplay: totalWatchDisplay,
      listenDisplay: totalListenDisplay,
      sessionsDisplay: totalSessionsDisplay
    };
  }, [summary, cameraData, radioData]);

  // Camera drilldown view
  if (selectedCamera) {
    return (
      <div className="space-y-6" data-testid="analytics-drilldown-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Camera Details</h1>
            <p className="text-muted-foreground">Detailed viewing statistics</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={handleTimeRangeChange}>
              <SelectTrigger className="w-36">
                <Calendar className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <CameraDrilldown 
          camera={selectedCamera} 
          timeRange={timeRange}
          onBack={() => setSelectedCamera(null)} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header Toolbar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground">Viewing statistics and trends</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="w-36" data-testid="time-range-select">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchAnalytics}
            disabled={isLoading}
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center gap-4 p-4">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <div className="flex-1">
              <p className="font-medium text-foreground">Failed to load analytics</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAnalytics}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="border-border bg-card">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <Eye className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {isLoading ? '...' : summaryStats.watchDisplay}
              </p>
              <p className="text-sm text-muted-foreground">Total Watch Time</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10">
              <Radio className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {isLoading ? '...' : summaryStats.listenDisplay}
              </p>
              <p className="text-sm text-muted-foreground">Total Listen Time</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
              <Users className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {isLoading ? '...' : summaryStats.sessionsDisplay}
              </p>
              <p className="text-sm text-muted-foreground">Total Sessions</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="cameras" data-testid="tab-cameras">
            <Camera className="mr-2 h-4 w-4" />
            Cameras
          </TabsTrigger>
          <TabsTrigger value="radio" data-testid="tab-radio">
            <Radio className="mr-2 h-4 w-4" />
            Radio
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cameras" className="mt-6">
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Top Cameras by Watch Hours
              </CardTitle>
              <CardDescription>
                Click a camera to see detailed daily statistics
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-12 animate-pulse rounded bg-muted" />
                  ))}
                </div>
              ) : (
                <CameraAnalyticsTable 
                  data={cameraData} 
                  onDrilldown={setSelectedCamera}
                />
              )}
            </CardContent>
          </Card>

          {/* Camera Watch Hours Chart */}
          {!isLoading && Array.isArray(cameraData) && cameraData.length > 0 && (
            <Card className="border-border bg-card mt-6">
              <CardHeader>
                <CardTitle>Watch Hours Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cameraData.slice(0, 8).map(c => ({
                      ...c,
                      // Normalize to minutes for chart
                      chart_minutes: getNum(c, ['watch_hours', 'total_watch_hours', 'hours']) !== null
                        ? getNum(c, ['watch_hours', 'total_watch_hours', 'hours']) * 60
                        : getNum(c, ['watch_minutes', 'total_minutes', 'minutes']) || 0
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(217.2 32% 17.5%)" />
                      <XAxis 
                        dataKey="name" 
                        stroke="hsl(215 20% 65%)"
                        fontSize={11}
                        angle={-20}
                        textAnchor="end"
                        height={60}
                        tick={{ fill: 'hsl(215 20% 65%)' }}
                      />
                      <YAxis 
                        stroke="hsl(215 20% 65%)"
                        fontSize={12}
                        tickFormatter={(v) => formatHoursValue(v, 'minutes')}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="chart_minutes" 
                        name="Watch Time"
                        fill="#F7931E" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="radio" className="mt-6">
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Radio className="h-5 w-5" />
                Top Stations by Listen Hours
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-12 animate-pulse rounded bg-muted" />
                  ))}
                </div>
              ) : (
                <RadioAnalyticsTable data={radioData} />
              )}
            </CardContent>
          </Card>

          {/* Radio Listen Hours Chart */}
          {!isLoading && Array.isArray(radioData) && radioData.length > 0 && (
            <Card className="border-border bg-card mt-6">
              <CardHeader>
                <CardTitle>Listen Hours Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={radioData.map(r => ({
                      ...r,
                      chart_minutes: getNum(r, ['listen_hours', 'total_listen_hours', 'hours']) !== null
                        ? getNum(r, ['listen_hours', 'total_listen_hours', 'hours']) * 60
                        : getNum(r, ['listen_minutes', 'total_minutes', 'minutes']) || 0
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(217.2 32% 17.5%)" />
                      <XAxis 
                        dataKey="name" 
                        stroke="hsl(215 20% 65%)"
                        fontSize={12}
                      />
                      <YAxis 
                        stroke="hsl(215 20% 65%)"
                        fontSize={12}
                        tickFormatter={(v) => formatHoursValue(v, 'minutes')}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="chart_minutes" 
                        name="Listen Time"
                        fill="#F59E0B" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Analytics;
